import { addToast } from "@heroui/react";
import { Button, ButtonGroup, type ButtonProps } from "@heroui-v3/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Check, Copy } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

type CopyButtonProps = Omit<
  ButtonProps,
  "onPress" | "isIconOnly" | "children"
> & {
  text: string;
  children?: React.ReactNode;
  withButtonGroupSeparator?: boolean;
  onCopy?: () => void;
};

export const CopyButton = ({ text, children, withButtonGroupSeparator = false, onCopy, ...props }: CopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    const markCopied = () => {
      setIsCopied(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      onCopy?.();
    };

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(text);
      markCopied();
    } catch {
      // Clipboard API requires a secure context (HTTPS/localhost). On HTTP
      // preview deployments it's unavailable — log the value so it can be
      // grabbed from devtools instead of failing silently.
      console.log("[CopyButton] clipboard unavailable, value:", text);
      addToast({
        title: t`Kopiranje ni na voljo, povezava je v konzoli`,
        color: "warning",
      });
      markCopied();
    }
  };

  if (children != null) {
    return (
      <Button {...props} onPress={handleCopy}>
        {withButtonGroupSeparator ? <ButtonGroup.Separator /> : null}
        <AnimatePresence initial={false} mode="wait">
          {isCopied ? (
            <motion.div
              key="check"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Check size={16} />
            </motion.div>
          ) : (
            <motion.div
              key="copy"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Copy size={16} />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence initial={false} mode="wait">
          {isCopied ? (
            <motion.div
              key="copied-text"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              layout
            >
              <Trans>Kopirano</Trans>
            </motion.div>
          ) : (
            <motion.div
              key="uncopied-text"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              layout
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </Button>
    );
  }

  return (
    <Button
      {...props}
      onPress={handleCopy}
      isIconOnly
      aria-label={isCopied ? t`Kopirano` : t`Kopiraj`}
    >
      {withButtonGroupSeparator ? <ButtonGroup.Separator /> : null}
      <AnimatePresence initial={false} mode="wait">
        {isCopied ? (
          <motion.div
            key="check"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Check size={16} />
          </motion.div>
        ) : (
          <motion.div
            key="copy"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Copy size={16} />
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};
